// ─── Système d'énergie Duel — stockage Supabase (Beta v1) ─────────────────────
// Plus de localStorage : un joueur ne peut plus reset son énergie en vidant son cache.
// Timer : duel_energy_updated_at en DB. Régénération calculée côté client à partir
// de ce timestamp (même logique qu'avant, mais source de vérité = Supabase).
//
// TODO avant release publique : remplacer spendDuelEnergy (get+patch) par une
// RPC Supabase atomique `spend_duel_energy(player_name)` pour éviter double-spend.
// La RPC calculera la regen, vérifiera l'énergie et décrementera en une seule
// transaction DB.

import { sb } from "../../../lib/supabase";

export const MAX_ENERGY = 8;
export const REGEN_MS = 30 * 60 * 1000; // 30min par point
export const MATCH_COST = 1;

export interface EnergyState {
  energy: number;    // 0..MAX_ENERGY
  msToNext: number;  // ms avant le prochain point (0 si plein)
  isFull: boolean;
}

/** Calcule l'énergie régénérée depuis un timestamp donné (pure, pas de réseau). */
export function calculateRegeneratedEnergy(
  base: number,
  updatedAt: Date | string,
  now: number = Date.now()
): EnergyState {
  const updatedMs = typeof updatedAt === "string" ? new Date(updatedAt).getTime() : updatedAt.getTime();
  const elapsed = Math.max(0, now - updatedMs);
  const regen = Math.floor(elapsed / REGEN_MS);
  const energy = Math.min(MAX_ENERGY, base + regen);
  if (energy >= MAX_ENERGY) return { energy: MAX_ENERGY, msToNext: 0, isFull: true };
  return { energy, msToNext: REGEN_MS - (elapsed % REGEN_MS), isFull: false };
}

export function formatEnergyTime(ms: number): string {
  const m = Math.ceil(ms / 60000);
  if (m >= 60) return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
  return `${m}min`;
}

/** Charge l'énergie depuis Supabase et calcule la regen. */
export async function getDuelEnergy(playerName: string): Promise<EnergyState> {
  const enc = encodeURIComponent(playerName);
  const rows = await sb.get(
    `players?name=eq.${enc}&select=duel_energy,duel_energy_updated_at&limit=1`
  );
  const row = rows[0];
  if (!row || row.duel_energy == null || !row.duel_energy_updated_at) {
    console.warn("[Energy] player not found or missing energy columns — returning full");
    return { energy: MAX_ENERGY, msToNext: 0, isFull: true };
  }
  const state = calculateRegeneratedEnergy(row.duel_energy, row.duel_energy_updated_at);
  console.log(
    `[Energy] loaded from Supabase — base: ${row.duel_energy}, regenerated: ${state.energy - row.duel_energy < 0 ? 0 : state.energy - row.duel_energy}, current: ${state.energy}`
  );
  return state;
}

/**
 * Tente de dépenser 1 énergie.
 * Retourne { success: true, state } ou { success: false, state } si vide.
 *
 * Préserve le timer de régénération (comme l'ancien localStorage).
 *
 * TODO anti-cheat : remplacer par RPC atomique `spend_duel_energy(player_name)`
 * avant release publique pour éviter le double-spend concurrent.
 */
export async function spendDuelEnergy(
  playerName: string,
  now: number = Date.now()
): Promise<{ success: boolean; state: EnergyState }> {
  const enc = encodeURIComponent(playerName);
  const rows = await sb.get(
    `players?name=eq.${enc}&select=duel_energy,duel_energy_updated_at&limit=1`
  );
  const row = rows[0];
  if (!row || row.duel_energy == null || !row.duel_energy_updated_at) {
    console.warn("[Energy] spend refused — player not found");
    return { success: false, state: { energy: 0, msToNext: REGEN_MS, isFull: false } };
  }

  const updatedMs = new Date(row.duel_energy_updated_at).getTime();
  const elapsed = Math.max(0, now - updatedMs);
  const regen = Math.floor(elapsed / REGEN_MS);
  const energy = Math.min(MAX_ENERGY, row.duel_energy + regen);

  if (energy < MATCH_COST) {
    console.log(`[Energy] spend refused, not enough energy (current: ${energy})`);
    const state = calculateRegeneratedEnergy(row.duel_energy, row.duel_energy_updated_at, now);
    return { success: false, state };
  }

  const newEnergy = energy - MATCH_COST;

  // Préserve le timer : ne pas remettre à zéro si pas encore plein.
  let newUpdatedAt: string;
  if (energy >= MAX_ENERGY) {
    // Était plein → timer démarre maintenant.
    newUpdatedAt = new Date(now).toISOString();
  } else {
    // Préserve la progression partielle dans la période courante.
    const partialMs = elapsed % REGEN_MS;
    newUpdatedAt = new Date(now - partialMs).toISOString();
  }

  await sb.patch("players", `name=eq.${enc}`, {
    duel_energy: newEnergy,
    duel_energy_updated_at: newUpdatedAt,
  });

  const state = calculateRegeneratedEnergy(newEnergy, newUpdatedAt, now);
  console.log(`[Energy] spend success — saved: energy=${newEnergy}, updated_at=${newUpdatedAt}`);
  return { success: true, state };
}
